import axios from "axios";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getClearContractorModel,
  getClientFilteringOptions,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import AddOrEditContractor from "./components/AddOrEditContractor";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDatalabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Contractor() {
  const {
    CONTRACTOR_API,
    contractorModel,
    setContractorModel,
    contractorsData,
    config,
    pageIndexContractors,
    setPageIndexContractors,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    setCity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [contractorDataFiltered, setContractorDataFiltered] = useState(
    contractorsData.Data,
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getClientFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const contractorDataObj = contractorsData.Data.find(
        (contractorObj) => contractorObj.Id === Number(id),
      );
      setContractorModel(contractorDataObj);
    }
  }, [id]);

  useEffect(() => {
    setContractorDataFiltered(contractorsData.Data);
  }, [contractorsData]);

  const validateOperationResult = (result, isDelete) => {
    setContractorModel(getClearContractorModel());
    if (!result.ok && !result.Ok && !result.data?.Ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([CONTRACTOR_API, config]);
    if (isDelete) {
      if (pageIndexContractors !== 0 && contractorsData.Data.length === 1) {
        setPageIndexContractors(pageIndexContractors - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${CONTRACTOR_API}/${itemId}`}`,
      config,
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const formData = new FormData();
    const file = data.File;

    formData.append(
      "requestData",
      new Blob(
        [
          JSON.stringify({
            ...data,
            File: undefined,
            IdentificationType: data.IdentificationType.Id,
            reportSetup: data.reportSetup
              ? JSON.stringify(data.reportSetup)
              : undefined,
          }),
        ],
        {
          type: "application/json",
        },
      ),
    );

    if (file) {
      formData.append("document", file, file.name);
    }

    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    };

    const updateResult = await axios.put(
      `${`${CONTRACTOR_API}/${data.Id}`}`,
      formData,
      requestConfig,
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const formData = new FormData();
    const file = data.File;

    formData.append(
      "requestData",
      new Blob(
        [
          JSON.stringify({
            ...data,
            File: undefined,
            IdentificationType: data.IdentificationType.Id,
          }),
        ],
        {
          type: "application/json",
        },
      ),
    );

    if (file) {
      formData.append("document", file, file.name);
    }

    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    };

    const updateResult = await axios.post(
      `${`${CONTRACTOR_API}`}`,
      formData,
      requestConfig,
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !contractorModel.IdentificationType ||
    !contractorModel.Name ||
    contractorModel.Name.replaceAll(" ", "").length <= 0 ||
    !contractorModel.Identification ||
    contractorModel.Identification.replaceAll(" ", "").length <= 0 ||
    !contractorModel.Address ||
    contractorModel.Address.replaceAll(" ", "").length <= 0 ||
    !contractorModel.PhoneNumber ||
    contractorModel.PhoneNumber.replaceAll(" ", "").length <= 0 ||
    contractorModel.StateId <= 0 ||
    contractorModel.CityId <= 0;

  const getColumnTitles = () => {
    return [
      localize.identification_type,
      localize.identification,
      localize.name,
      localize.phone_number,
      localize.email,
      localize.contact_name,
      localize.address,
      localize.department,
      localize.city,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.contractors}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={CONTRACTOR_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="contractor"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_contractor}
          isEditForm
          onClickToSubmit={async () => {
            await handleUpdateRequest(contractorModel);
            setShowEditModal(false);
            setCity(null);

            setContractorModel(getClearContractorModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setCity(null);

            setContractorModel(getClearContractorModel());
          }}
          Component={<AddOrEditContractor />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.contractors}
            subtitle={localize.contractors_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="contractor_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_contractor}
                onClickToSubmit={async () => {
                  await handleCreateRequest(contractorModel);
                  setShowAddModal(false);
                  setCity(null);
                  setContractorModel(getClearContractorModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setCity(null);
                  setContractorModel(getClearContractorModel());
                }}
                Component={<AddOrEditContractor />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={contractorModel.Name}
        entityType={localize.contractor}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setContractorModel(getClearContractorModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(contractorModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexContractors}
            setPageIndex={setPageIndexContractors}
            dataArray={contractorsData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          contractorsData.Data?.length !== 0
            ? contractorDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setContractorModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          reportSetup: item?.reportSetup
                            ? JSON.parse(item.reportSetup)
                            : undefined,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setContractorModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                        });
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  <ValueOrNoDatalabel data={item.IdentificationType} />,
                  <ValueOrNoDatalabel data={item.Identification} />,
                  <ValueOrNoDatalabel data={item.Name} />,
                  <ValueOrNoDatalabel data={item.PhoneNumber} />,
                  <ValueOrNoDatalabel data={item.Email} />,
                  <ValueOrNoDatalabel data={item.ContactName} />,
                  <ValueOrNoDatalabel data={item.Address} />,
                  <ValueOrNoDatalabel data={item.State?.Name} />,
                  <ValueOrNoDatalabel data={item.City?.Name} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(12)]
        }
      />
    </div>
  );
}
